import * as THREE from 'three'

import { DEBUG_MODE } from '../../config/debug'

// ============================================================
// Movimento della camera "a corpo" (ispirato ai bodycam shooter):
// respiro da fermo, rimbalzo e oscillazione del passo, impatto del
// piede a terra, rollio in curva per inerzia e micro-tremore.
//
// PERCHÉ UN RIG E NON LA CAMERA DIRETTAMENTE
// La camera è figlia di un gruppo ("rig") che il Player posiziona
// sul corpo. Le scosse vanno sul RIG, mai sulla camera:
//  • ruotare il rig, che sta ADDOSSO alla camera, la fa girare su sé
//    stessa (prima il gruppo stava nell'origine del mondo e la
//    faceva orbitare: l'oscillazione cresceva allontanandosi dal
//    centro del negozio — la causa dell'effetto finto);
//  • la rotazione della camera resta di esclusiva competenza dei
//    PointerLockControls, che altrimenti riassorbirebbero la scossa
//    nella direzione di sguardo facendola derivare nel tempo.
// Le scosse sono espresse nel frame della VISTA (solo yaw), così
// "oscillare a destra" è sempre a destra di dove stai guardando.
// ============================================================

const SU = new THREE.Vector3(0, 1, 0)

// Parametri, aggiornati dagli slider Leva in CameraController
export const cfgCamera = {
  attivo: true,
  respiro: 1,
  passo: 1,
  inerzia: 1,
  tremore: 1,
  // Istante fino al quale il mouse è in SORDINA: dopo ogni aggancio del
  // pointer lock Chrome consegna un primo mousemove con delta enorme, e
  // CameraController azzera la sensibilità per quella finestra. Sta qui
  // e non lì dentro perché serve anche a chi deve distinguere un
  // movimento VERO del giocatore da quel colpo a vuoto — vedi il
  // ritorno della visuale in Player.jsx.
  sordinaFino: 0,
}

// Il rig viene registrato da CameraController e usato dal Player
let rig = null
export function registraRig(gruppo) {
  rig = gruppo
}
export function getRig() {
  return rig
}

// Stato continuo del movimento (fase del passo, inerzie, impatti)
const stato = {
  fasePasso: 0,
  intensita: 0, // 0 fermo → 1 in movimento, con transizione morbida
  corsa: 0,
  impatto: 0,
  impattoLisc: 0,
  piedeDestro: false,
  yawPrec: null,
  pitchPrec: null,
  inerziaYaw: 0,
  inerziaPitch: 0,
  lateraleLisc: 0,
}

// In ?debug: stato e rig ispezionabili da console (window.__camera)
if (DEBUG_MODE && typeof window !== 'undefined') {
  window.__camera = { stato, cfg: cfgCamera, getRig: () => rig }
}

const tmp = {
  dir: new THREE.Vector3(),
  destra: new THREE.Vector3(),
  qYaw: new THREE.Quaternion(),
  qYawInv: new THREE.Quaternion(),
  qScossa: new THREE.Quaternion(),
  eul: new THREE.Euler(),
  off: new THREE.Vector3(),
}

// Ampiezze di base (metri e radianti): valori piccoli e realistici,
// gli slider Leva li moltiplicano
const A = {
  respiroY: 0.011,
  respiroPitch: 0.004,
  bobY: 0.017,
  swayX: 0.013,
  rollPasso: 0.008,
  pitchPasso: 0.003,
  impattoY: 0.008,
  impattoRoll: 0.004,
  rollStrafe: 0.035,
  tremorePos: 0.0012,
  tremoreRot: 0.0016,
}

// Rumore morbido: somma di seni a frequenze incommensurabili, così
// non si ripete e non "scatta" come farebbe Math.random a ogni frame
function tremolio(t, fase) {
  return (
    Math.sin(t * 1.7 + fase) * 0.5 +
    Math.sin(t * 2.9 + fase * 2.1) * 0.3 +
    Math.sin(t * 4.3 + fase * 3.7) * 0.2
  )
}

/**
 * Applica il movimento della camera. Va chiamata DOPO aver
 * posizionato il rig sul corpo (lo fa il Player, il cui useFrame
 * gira dopo quello di CameraController).
 *
 * @param camera   la camera (la sua rotazione NON viene toccata)
 * @param delta    secondi dall'ultimo frame
 * @param t        tempo assoluto
 * @param moto     { muove: bool, corre: bool, laterale: -1..1 }
 */
export function aggiornaCamera(camera, delta, t, moto) {
  if (!rig) return
  if (!cfgCamera.attivo) {
    rig.quaternion.identity()
    camera.position.set(0, 0, 0)
    return
  }

  // --- transizioni morbide tra fermo, camminata e corsa ---
  const k = 1 - Math.exp(-8 * delta)
  stato.intensita += ((moto.muove ? 1 : 0) - stato.intensita) * k
  stato.corsa += ((moto.corre ? 1 : 0) - stato.corsa) * k
  stato.lateraleLisc += (moto.laterale - stato.lateraleLisc) * k

  let offX = 0
  let offY = 0
  let pitch = 0
  let yaw = 0
  let roll = 0

  // --- RESPIRO (svanisce quando ci si muove) ---
  // Onda asimmetrica: l'inspirazione è più rapida dell'espirazione,
  // per questo la seconda armonica non è in fase con la prima.
  const fasePolmoni = t * Math.PI * 2 * 0.23 // ~14 respiri al minuto
  const respiro =
    Math.sin(fasePolmoni) * 0.62 + Math.sin(fasePolmoni * 2 + 0.9) * 0.16
  const pesoRespiro = (1 - stato.intensita) * cfgCamera.respiro
  offY += respiro * A.respiroY * pesoRespiro
  pitch += respiro * A.respiroPitch * pesoRespiro

  // --- PASSO ---
  // CONVENZIONE DELLA FASE: un PASSO = PI radianti, una FALCATA
  // (passo destro + sinistro) = 2*PI. Da qui discende tutto il resto,
  // e sbagliarla raddoppia la frequenza di ogni oscillazione.
  // `moto.ritmo` rallenta la cadenza quando il corpo cammina piu' piano del
  // normale (da accovacciati): il passo della camera deve battere insieme
  // ai piedi che si vedono, non al ritmo di una camminata in piedi.
  const passiAlSecondo = (1.85 + stato.corsa * 1.05) * (moto.ritmo ?? 1) // ~1.9 a piedi, ~2.9 di corsa
  const fasePrec = stato.fasePasso
  stato.fasePasso += delta * passiAlSecondo * Math.PI * stato.intensita

  // impatto: a ogni multiplo di PI un piede tocca terra
  if (Math.floor(stato.fasePasso / Math.PI) > Math.floor(fasePrec / Math.PI)) {
    stato.impatto = 1
    stato.piedeDestro = !stato.piedeDestro
  }
  // La fase si riavvolge sulla falcata: senza, dopo qualche minuto i
  // valori diventano grandi e i seni perdono precisione (scatti).
  if (stato.fasePasso > Math.PI * 2) stato.fasePasso -= Math.PI * 2

  // L'impulso decade in fretta, ma viene poi filtrato: un gradino
  // secco da 0 a 1 in un frame si legge come un colpo di martello,
  // non come un piede che appoggia.
  stato.impatto *= Math.exp(-delta * 9)
  stato.impattoLisc +=
    (stato.impatto - stato.impattoLisc) * (1 - Math.exp(-delta * 20))

  const ampiezzaPasso =
    stato.intensita * (1 + stato.corsa * 0.3) * cfgCamera.passo

  // Rimbalzo verticale, uno per passo (periodo PI).
  // sin^2 e non |sin|: hanno la stessa forma e lo stesso periodo, ma
  // il valore assoluto ha una cuspide a ogni appoggio — derivata
  // discontinua, che l'occhio legge come uno strappo.
  // Testa alta a meta appoggio, bassa quando il piede tocca: la parte
  // costante si toglie per restare centrati sull'altezza occhi.
  const sinPasso = Math.sin(stato.fasePasso)
  offY += (sinPasso * sinPasso - 0.5) * A.bobY * ampiezzaPasso
  // oscillazione laterale: una per falcata completa (periodo 2*PI)
  offX += sinPasso * A.swayX * ampiezzaPasso
  // rollio accoppiato all'oscillazione + micro-cenno del capo
  roll -= sinPasso * A.rollPasso * ampiezzaPasso
  pitch += Math.sin(stato.fasePasso * 2) * A.pitchPasso * ampiezzaPasso

  // contraccolpo del piede a terra (gia smussato dal filtro sopra)
  const imp = stato.impattoLisc * ampiezzaPasso
  offY -= imp * A.impattoY
  pitch += imp * A.impattoY * 0.4
  roll += imp * A.impattoRoll * (stato.piedeDestro ? 1 : -1)

  // --- DIREZIONE DI SGUARDO ---
  // Ricavata dal vettore, non da camera.rotation: i PointerLockControls
  // impostano il QUATERNIONE partendo da un euler YXZ, mentre
  // camera.rotation viene decomposta con l'ordine proprio della camera
  // (XYZ) — leggere .y/.x da lì non darebbe il vero yaw/pitch.
  // Si usa il quaternione LOCALE: quello mondiale conterrebbe la
  // scossa del rig e si creerebbe un anello di retroazione.
  tmp.dir.set(0, 0, -1).applyQuaternion(camera.quaternion)
  const pitchVista = Math.asin(THREE.MathUtils.clamp(tmp.dir.y, -1, 1))
  // Lo yaw dall'asse DESTRO e non da quello di sguardo: guardando a
  // piombo la direzione è verticale e le sue componenti orizzontali
  // affogano nel rumore di arrotondamento, cosicché l'atan2 sputa un
  // angolo a caso. L'asse destro resta orizzontale a ogni inclinazione.
  // (stessa correzione in Player.jsx: se si tocca una, toccare l'altra)
  tmp.destra.set(1, 0, 0).applyQuaternion(camera.quaternion)
  const yawVista = Math.atan2(-tmp.destra.z, tmp.destra.x)

  // --- INERZIA DELLA VISTA (il tratto più "bodycam") ---
  // La camera è legata al corpo: quando giri di scatto resta un
  // attimo indietro e rolla nella curva, poi rientra.
  if (stato.yawPrec === null) {
    stato.yawPrec = yawVista
    stato.pitchPrec = pitchVista
  }
  let dYaw = yawVista - stato.yawPrec
  if (dYaw > Math.PI) dYaw -= Math.PI * 2
  if (dYaw < -Math.PI) dYaw += Math.PI * 2
  const dPitch = pitchVista - stato.pitchPrec
  stato.yawPrec = yawVista
  stato.pitchPrec = pitchVista

  stato.inerziaYaw = THREE.MathUtils.clamp(stato.inerziaYaw + dYaw, -0.35, 0.35)
  stato.inerziaPitch = THREE.MathUtils.clamp(stato.inerziaPitch + dPitch, -0.25, 0.25)
  stato.inerziaYaw *= Math.exp(-delta * 6)
  stato.inerziaPitch *= Math.exp(-delta * 7)

  yaw -= stato.inerziaYaw * 0.22 * cfgCamera.inerzia
  pitch -= stato.inerziaPitch * 0.2 * cfgCamera.inerzia
  roll -= stato.inerziaYaw * 0.3 * cfgCamera.inerzia

  // inclinazione nel passo laterale
  roll -= stato.lateraleLisc * A.rollStrafe * stato.intensita * cfgCamera.passo

  // --- MICRO-TREMORE: sempre presente, tiene "viva" l'immagine ---
  const tr = cfgCamera.tremore
  offX += tremolio(t, 0) * A.tremorePos * tr
  offY += tremolio(t, 2.4) * A.tremorePos * tr
  pitch += tremolio(t, 4.1) * A.tremoreRot * tr
  yaw += tremolio(t, 5.8) * A.tremoreRot * tr
  roll += tremolio(t, 1.3) * A.tremoreRot * 0.7 * tr

  // --- APPLICAZIONE ---
  // Tutto è espresso nel frame della vista (solo yaw): la
  // coniugazione porta la rotazione in spazio mondo mantenendo gli
  // assi relativi a dove stai guardando.
  tmp.qYaw.setFromAxisAngle(SU, yawVista)
  tmp.qYawInv.copy(tmp.qYaw).invert()
  tmp.eul.set(pitch, yaw, roll, 'YXZ')
  tmp.qScossa.setFromEuler(tmp.eul)
  rig.quaternion.copy(tmp.qYaw).multiply(tmp.qScossa).multiply(tmp.qYawInv)

  tmp.off.set(offX, offY, 0).applyQuaternion(tmp.qYaw)
  camera.position.copy(tmp.off)
}
